<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import V2QrOptionsPane from '@/components/pages/qr/V2QrOptionsPane.vue';
  import V2QrPreviewPane from '@/components/pages/qr/V2QrPreviewPane.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useMenuQrCopy } from '@/composition/menu-qr/use-menu-qr-copy';
  import { useMenuQrExport } from '@/composition/menu-qr/use-menu-qr-export';
  import { useMenuQrPreview } from '@/composition/menu-qr/use-menu-qr-preview';
  import { useMenuQrTables } from '@/composition/menu-qr/use-menu-qr-tables';
  import { useAuthStore } from '@/composition/use-auth';
  import { useUi } from '@/composition/use-ui';
  import {
    TMenuQrDesignMode,
    toMenuQrOrderingContext,
  } from '@/domain/menu-qr/menu-qr-design';
  import { TMenuQrPosterSize } from '@/domain/menu-qr/menu-qr-poster';
  import {
    buildMenuQrTargets,
    buildMenuQrZipName,
    IMenuQrSelection,
    IMenuQrTargetsResult,
  } from '@/domain/menu-qr/menu-qr-targets';
  import { AlertType } from '@/interfaces/IAlert';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const EMPTY_TARGETS: IMenuQrTargetsResult = {
    targets: [],
    skippedTables: [],
  };

  const restaurantStore = useRestaurantStore();
  const authStore = useAuthStore();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const isPlatformAdmin = computed(() => !!authStore.user?.admin);
  const designMode = ref<TMenuQrDesignMode>('auto');
  const size = ref<TMenuQrPosterSize>('square');
  const selection = ref<IMenuQrSelection>({
    includeStorefront: true,
    includeTakeAway: false,
    includeTables: false,
    selectedTableIds: [],
  });

  const {
    copy,
    failed: copyFailed,
    languageName,
    usesMenuLanguage,
  } = useMenuQrCopy();
  const {
    tables,
    loading: tablesLoading,
    error: tablesError,
    reload: reloadTables,
  } = useMenuQrTables();

  // Every table of the branch starts selected.
  watch(tables, (loaded) => {
    selection.value = {
      ...selection.value,
      selectedTableIds: (loaded ?? []).map((table) => table.id),
    };
  });

  const ordering = computed(() =>
    toMenuQrOrderingContext(
      restaurantStore.restaurant,
      restaurantStore.selectedBranch
    )
  );
  const effectiveTier = computed(() => ordering.value.effectiveTier);
  const branchToken = computed(
    () => restaurantStore.selectedBranch?.token ?? null
  );
  const logoUrl = computed(() =>
    restaurantStore.restaurant?.logo
      ? `${Globals.uploadsPath}/${restaurantStore.restaurant.logo}`
      : null
  );

  const targetsResult = computed<IMenuQrTargetsResult>(() => {
    const restaurant = restaurantStore.restaurant;

    if (!restaurant || !branchToken.value || !copy.value) return EMPTY_TARGETS;

    return buildMenuQrTargets({
      restaurantId: restaurant.id,
      branchToken: branchToken.value,
      tables: tables.value ?? [],
      selection: selection.value,
      mode: designMode.value,
      ordering: ordering.value,
      copy: copy.value,
      names: {
        storefrontCaption: i18nT('qr.targets.storefront'),
        takeAwayCaption: i18nT('qr.targets.takeAway'),
        genericFile: i18nT('qr.fileName.generic'),
        takeAwayFile: i18nT('qr.fileName.takeaway'),
        tablesFolder: i18nT('qr.fileName.tables'),
      },
    });
  });
  const targets = computed(() => targetsResult.value.targets);
  const skippedTables = computed(() => targetsResult.value.skippedTables);
  const zipName = computed(() =>
    buildMenuQrZipName(
      restaurantStore.restaurant?.name ?? '',
      restaurantStore.selectedBranch?.name ?? 'Principal'
    )
  );

  const {
    imageUrl,
    current: previewTarget,
    index: previewIndex,
    total: previewTotal,
    rendering,
    failed,
    next,
    prev,
  } = useMenuQrPreview({ targets, size, copy, logoUrl });

  const {
    downloading,
    progress,
    download,
    printingStickers,
    printStickers,
    openStickerSettings,
  } = useMenuQrExport({ targets, skippedTables, size, copy, logoUrl, zipName });

  // Vue 2 runtime prop checks reject null-typed props, so panes receive undefined.
  const previewCurrent = computed(() => previewTarget.value ?? undefined);
  const tablesForPane = computed(() => tables.value ?? undefined);
  const branchTokenMissing = computed(
    () => !!restaurantStore.selectedBranch && !branchToken.value
  );
  const previewReady = computed(() => !!copy.value);
  const previewFailed = computed(
    () => failed.value || copyFailed.value || branchTokenMissing.value
  );
  const busy = computed(() => downloading.value || printingStickers.value);
  const canAct = computed(
    () => targets.value.length > 0 && !!branchToken.value && !busy.value
  );
  const downloadLabel = computed(() =>
    progress.value
      ? i18nT('qr.download.progress', { ...progress.value })
      : i18nT('qr.download')
  );

  watch(
    () => restaurantStore.selectedBranch?.id,
    () => {
      const branch = restaurantStore.selectedBranch;

      if (branch && !branch.token) {
        ui.showToast(AlertType.Error, i18nT('qr.error.branchToken'));
      }
    },
    { immediate: true }
  );
</script>

<template>
  <div
    class="qr-page"
    :data-copy-locale="copy ? copy.locale : ''"
    :data-copy-voseo="copy && copy.voseo ? 'true' : 'false'"
  >
    <div class="qr-page-inner">
      <div class="command-bar">
        <span class="page-title">{{ $t('qr.title') }}</span>

        <div class="command-bar-controls">
          <PosUiButton
            v-if="isPlatformAdmin"
            color="neutral"
            variant="ghost"
            size="sm"
            icon="cog"
            :title="$t('qr.stickers.settings')"
            @click="openStickerSettings"
          />
          <PosUiButton
            v-if="isPlatformAdmin"
            color="neutral"
            variant="outline"
            size="sm"
            icon="printer"
            :title="$t('qr.stickers.print')"
            :loading="printingStickers"
            :disabled="!canAct"
            @click="printStickers"
          >
            {{ $t('qr.stickers.print') }}
          </PosUiButton>
          <PosUiButton
            color="primary"
            size="sm"
            icon="download"
            :title="downloadLabel"
            :loading="downloading"
            :disabled="!canAct"
            @click="download"
          >
            {{ downloadLabel }}
          </PosUiButton>
        </div>
      </div>

      <div class="qr-body">
        <V2QrPreviewPane
          class="preview-pane"
          :image-url="imageUrl"
          :current="previewCurrent"
          :index="previewIndex"
          :total="previewTotal"
          :ready="previewReady"
          :rendering="rendering"
          :failed="previewFailed"
          @prev="prev"
          @next="next"
        />

        <V2QrOptionsPane
          class="options-pane"
          :design-mode="designMode"
          :size="size"
          :selection="selection"
          :effective-tier="effectiveTier"
          :ordering="ordering"
          :tables="tablesForPane"
          :tables-loading="tablesLoading"
          :tables-error="tablesError"
          :skipped-tables="skippedTables"
          :language-name="languageName"
          :language-from-menu="usesMenuLanguage"
          @update:design-mode="designMode = $event"
          @update:size="size = $event"
          @update:selection="selection = $event"
          @retry-tables="reloadTables"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .qr-page {
    @apply tw-flex tw-h-full tw-justify-center tw-px-4 tw-py-4 md:tw-px-6;

    .qr-page-inner {
      // overflow-clip (not hidden) keeps the rounded clipping without creating a
      // scroll container, so the preview can stick while the document scrolls.
      @apply tw-flex tw-w-full tw-max-w-7xl tw-flex-col tw-overflow-clip tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

      .command-bar {
        @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-3;

        .page-title {
          @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
        }

        .command-bar-controls {
          @apply tw-ml-auto tw-flex tw-flex-wrap tw-items-center tw-gap-2;

          // Icon-only buttons below md: hide the primitive's label wrapper (and its gap).
          :deep(.pos-ui-button-label) {
            @apply tw-hidden md:tw-inline-flex;
          }

          // Keep the progress text visible on phones while a download runs.
          :deep(.pos-ui-button.is-loading .pos-ui-button-label) {
            @apply tw-inline-flex;
          }
        }
      }

      // Content pages scroll with the document; the preview sticks below the 48px app bar.
      .qr-body {
        @apply tw-flex tw-flex-col md:tw-grid md:tw-grid-cols-[320px_minmax(0,1fr)] lg:tw-grid-cols-[400px_minmax(0,1fr)];

        .preview-pane {
          @apply tw-p-4 md:tw-order-2 md:tw-sticky md:tw-top-12 md:tw-self-start md:tw-p-6;
        }

        .options-pane {
          @apply tw-p-4 md:tw-order-1 md:tw-border-r md:tw-border-neutral-200 md:tw-p-6;
        }
      }
    }
  }
</style>
